import { useState } from 'react';
import type { JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyState } from '@ndrc/ui';
import { useClassFile } from '../lib/queries';
import { ApiError, mutateCore } from '../lib/rpc';
import { t } from '../messages';
import type { MessageKey } from '../messages';
import { useDesk } from './context';

/**
 * **« Aucun espace de travail » — écrit pour celui qui le lit.**
 *
 * Deux écrans du cœur affichent ce refus : l'hôte d'application (`host/AppHost.tsx`, l'outil qui
 * s'ouvre sur rien) et le compte rendu de vérification (`activity/ActivityBoard.tsx`, rien à
 * relire). Ils partagent ce composant parce qu'ils partagent la question — *pourquoi* n'y a-t-il
 * pas d'espace de travail — et que la réponse n'est pas la même selon qui regarde.
 *
 * Trois lecteurs, trois vérités, et aucune n'est une variante de politesse :
 *
 * 1. **Un élève** ne peut rien y faire : c'est son enseignant qui distribue les fichiers. Sa phrase
 *    est celle de `messages/fr.json` sous la clé demandée, inchangée depuis toujours.
 * 2. **Un enseignant dont la classe n'a encore aucun fichier** lit ce qu'il peut faire, et le fait
 *    d'un clic (`.teacher`, résolu par `t()` — voir `src/messages.ts`).
 * 3. **Un enseignant dont la classe A un fichier** lit autre chose, et c'est le cas le plus
 *    fréquent : il tombe sur cet écran *tous les jours*, parce qu'un espace de travail appartient
 *    toujours à un élève et qu'aucun n'est créé pour l'adulte (`resolveDefaultWorkspace`,
 *    `@ndrc/core-domain` : `owner_user_id = <l'acteur>`). Lui dire « vous n'avez pas encore
 *    distribué de fichier » serait un mensonge le lendemain du jour où il en a distribué un — le
 *    défaut qu'on répare ici, retourné. On le lit donc à `classes.file` avant de parler.
 *
 * La lecture de `classes.file` est réservée au rôle enseignant côté serveur : elle n'est demandée
 * que lorsqu'un enseignant regarde (`enabled` via un `classId` volontairement laissé `undefined`
 * sinon), jamais « au cas où ».
 */
export function NoWorkspace({
  titleKey,
  hintKey,
}: {
  /** Le titre, et la phrase, tels que l'élève les lit. `t()` sert d'elle-même la rédaction
   * `.teacher` quand c'est un adulte qui regarde et qu'elle existe. */
  titleKey: MessageKey;
  hintKey: MessageKey;
}): JSX.Element {
  const { me, classId, openTeacher } = useDesk();
  const queryClient = useQueryClient();
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isTeacher = me.role === 'teacher';

  // La même résolution que partout ailleurs dans la coque : `classId` n'est posé que lorsqu'il a
  // été réellement CHOISI (enseignant multi-classe) ; sinon la classe est la seule qu'on ait.
  const shownClassId = classId ?? me.classes[0]?.id;
  const classFile = useClassFile(isTeacher ? shownClassId : undefined);

  /** `workspace.demo` crée (ou rouvre) l'espace de démonstration de cet enseignant pour cette
   * classe, puis fait relire au bureau l'espace qu'il résout : la fenêtre déjà ouverte se remplit
   * d'elle-même, sans rechargement — c'est `desk.resolveWorkspace` qui la nourrit. */
  async function prepareDemo(): Promise<void> {
    setPreparing(true);
    setError(null);
    try {
      await mutateCore(
        'workspace.demo',
        shownClassId === undefined ? {} : { classId: shownClassId },
      );
      await queryClient.invalidateQueries({ queryKey: ['core', 'desk.resolveWorkspace'] });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('desk.errorTitle'));
    } finally {
      setPreparing(false);
    }
  }

  if (!isTeacher) {
    return <EmptyState title={t(titleKey)} hint={t(hintKey)} />;
  }

  // Tant que `classes.file` n'a pas répondu, on affiche le titre et rien d'autre : les deux
  // phrases possibles se contredisent, et en faire clignoter une avant l'autre reviendrait à
  // affirmer quelque chose qu'on ne sait pas encore.
  const answered = classFile.data !== undefined || classFile.isError || shownClassId === undefined;
  if (!answered) return <EmptyState title={t(titleKey)} />;

  const company = classFile.data?.companyName ?? null;
  if (company !== null) {
    return (
      <EmptyState title={t(titleKey)} hint={t('workspace.teacherOwnCopy', { company })}>
        {/* **Le geste, à côté de la phrase** — et depuis le chantier 9, ce geste EXISTE. Cet écran
            constatait « vous n'avez pas de copie personnelle de ce fichier » et ne proposait que
            d'aller regarder l'espace d'un élève, c'est-à-dire de projeter le travail de quelqu'un
            devant sa classe. `workspace.demo` crée sa propre copie, peuplée du même fichier,
            invisible de tous : un clic, et l'outil qu'il vient d'ouvrir s'ouvre pour de bon.
            Un refus du cœur s'affiche ici, avec SA phrase — jamais avalé, jamais remplacé par un
            bouton qui resterait muet. */}
        {error === null ? null : <p className="ndrc-error">{error}</p>}
        <button
          type="button"
          className="ndrc-button ndrc-button--primary"
          disabled={preparing}
          onClick={() => {
            void prepareDemo();
          }}
        >
          {preparing ? t('workspace.demoWorking') : t('workspace.demoPrepare')}
        </button>
        <button
          type="button"
          className="ndrc-button"
          onClick={() => {
            openTeacher('class');
          }}
        >
          {t('workspace.openStudent')}
        </button>
      </EmptyState>
    );
  }

  return (
    <EmptyState title={t(titleKey)} hint={t(hintKey)}>
      <button
        type="button"
        className="ndrc-button ndrc-button--primary"
        onClick={() => {
          openTeacher('datasets');
        }}
      >
        {t('workspace.distribute')}
      </button>
    </EmptyState>
  );
}
